import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardHeader } from '@/components/Card';
import { Button } from '@/components/Button';
import { Field } from '@/components/Field';
import { Input } from '@/components/Input';
import { Badge } from '@/components/Badge';
import { api, ApiError } from '@/lib/api';
import { useActiveCompanyId } from '@/lib/active-company';

interface TeamMember {
  id: number;
  name: string;
  client: { id: number; name: string | null; email: string; emailVerified: boolean };
  createdAt: string;
}

const ROLE_LABEL: Record<string, string> = {
  company_admin: 'Admin',
  company_associate: 'Associate',
  read_only: 'Read-only',
};

export function TeamTab() {
  const companyId = useActiveCompanyId();
  const qc = useQueryClient();

  const team = useQuery({
    queryKey: ['team', companyId],
    queryFn: () => api.get<TeamMember[]>('/api/Team'),
    enabled: !!companyId,
  });

  const [inviteOpen, setInviteOpen] = useState(false);

  if (!companyId) return <p>Select a company first.</p>;

  return (
    <div className="flex flex-col gap-gap-element-lg">
      <Card>
        <CardHeader
          title="Team"
          description="Manage who can access this company's compliance program."
          action={
            <Button size="sm" onClick={() => setInviteOpen((v) => !v)}>
              {inviteOpen ? 'Cancel' : 'Invite member'}
            </Button>
          }
        />

        {inviteOpen ? (
          <InviteForm
            companyId={companyId}
            onDone={() => {
              setInviteOpen(false);
              qc.invalidateQueries({ queryKey: ['team', companyId] });
            }}
          />
        ) : null}

        {team.isLoading ? (
          <p className="text-body text-text-secondary">Loading…</p>
        ) : team.data && team.data.length > 0 ? (
          <ul className="flex flex-col divide-y divide-border -mx-6 mt-4">
            {team.data.map((m) => (
              <MemberRow key={m.id} companyId={companyId} member={m} />
            ))}
          </ul>
        ) : (
          <p className="mt-4 text-body text-text-secondary">No team members yet.</p>
        )}
      </Card>
    </div>
  );
}

const inviteSchema = z.object({
  name: z.string().min(1, 'Required'),
  email: z.string().email('Enter a valid email'),
  role: z.enum(['company_admin', 'company_associate', 'read_only']),
});

type InviteValues = z.infer<typeof inviteSchema>;

function InviteForm({ onDone }: { companyId: number; onDone: () => void }) {
  const send = useMutation({
    mutationFn: (input: InviteValues) => api.post('/api/Team', input),
    onSuccess: onDone,
  });
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<InviteValues>({
    resolver: zodResolver(inviteSchema),
    defaultValues: { name: '', email: '', role: 'company_associate' },
  });
  return (
    <form
      onSubmit={handleSubmit((v) => send.mutate(v))}
      className="mt-4 grid gap-gap-element rounded-lg border border-border bg-background p-4 md:grid-cols-3"
    >
      <Field id="invite-name" label="Name" error={errors.name?.message}>
        <Input id="invite-name" {...register('name')} />
      </Field>
      <Field id="invite-email" label="Email" error={errors.email?.message}>
        <Input id="invite-email" type="email" {...register('email')} />
      </Field>
      <Field id="invite-role" label="Role" error={errors.role?.message}>
        <select
          id="invite-role"
          className="h-11 w-full rounded-md border border-border bg-surface px-3 text-body"
          {...register('role')}
        >
          <option value="company_associate">Associate</option>
          <option value="company_admin">Admin</option>
          <option value="read_only">Read-only</option>
        </select>
      </Field>

      {send.error ? (
        <div className="md:col-span-3 rounded-md border border-danger/40 bg-danger/5 px-4 py-3 text-body-sm text-danger">
          {send.error instanceof ApiError ? send.error.message : 'Could not invite'}
        </div>
      ) : null}

      <div className="md:col-span-3 flex justify-end">
        <Button type="submit" size="sm" disabled={send.isPending}>
          {send.isPending ? 'Sending invite…' : 'Send invite'}
        </Button>
      </div>
    </form>
  );
}

function MemberRow({ companyId, member }: { companyId: number; member: TeamMember }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [role, setRole] = useState(member.name);

  const update = useMutation({
    mutationFn: () => api.post(`/api/Team/${member.id}`, { role }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['team', companyId] });
      setEditing(false);
    },
  });

  const remove = useMutation({
    mutationFn: () => api.delete(`/api/Team/${member.id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['team', companyId] }),
  });

  return (
    <li className="flex flex-wrap items-center justify-between gap-3 px-6 py-4">
      <div>
        <p className="font-medium">{member.client.name ?? member.client.email}</p>
        <p className="text-body-sm text-text-secondary">{member.client.email}</p>
      </div>
      <div className="flex items-center gap-2">
        {editing ? (
          <>
            <select
              className="h-9 rounded-md border border-border bg-surface px-2 text-body-sm"
              value={role}
              onChange={(e) => setRole(e.target.value)}
            >
              <option value="company_associate">Associate</option>
              <option value="company_admin">Admin</option>
              <option value="read_only">Read-only</option>
            </select>
            <Button size="sm" onClick={() => update.mutate()} disabled={update.isPending}>
              Save
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
              Cancel
            </Button>
          </>
        ) : (
          <>
            <Badge tone={member.name === 'company_admin' ? 'accent' : 'default'}>
              {ROLE_LABEL[member.name] ?? member.name}
            </Badge>
            <Button size="sm" variant="ghost" onClick={() => setEditing(true)}>
              Edit
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                if (confirm(`Remove ${member.client.email}?`)) remove.mutate();
              }}
            >
              Remove
            </Button>
          </>
        )}
      </div>
    </li>
  );
}
